import Faqs from '@components/app/Faqs'
import { ListingSection } from '@components/app/ListingPage/TableOfContents/ListingSection'
import { NavBar } from '@components/app/NavBar'
import RelatedArticleLinks from '@components/app/RelatedLinkSection/RelatedArticleLinks'
import { RelatedConditionCarousel } from '@components/app/RelatedLinkSection/RelatedConditionCarousel'
import RelatedListingLinks from '@components/app/RelatedLinkSection/RelatedListingLinks'
import RelatedTreatmentTopicLinks from '@components/app/RelatedLinkSection/RelatedTreatmentTopicLinks'
import { TrialsForTreatment } from '@components/app/TreatmentPage/RelatedTrials'
import { ContraIndicationsSummary } from '@components/app/TreatmentPage/SectionDescriptions/ContraIndications'
import { DoseModificationSummary } from '@components/app/TreatmentPage/SectionDescriptions/DoseModification'
import { DrugInteractionTemplateString } from '@components/app/TreatmentPage/SectionDescriptions/DrugInteractions'
import { FDAApprovedTemplateString } from '@components/app/TreatmentPage/SectionDescriptions/FDAApproval'
import { NovelUsesSummary } from '@components/app/TreatmentPage/SectionDescriptions/NovelUses'
import { TreatmentPageTableOfContents } from '@components/app/TreatmentPage/TableOfContents'
import TreatmentActionProperties from '@components/app/TreatmentPage/TreatmentActionProperties'
import TreatmentApprovedProperties from '@components/app/TreatmentPage/TreatmentApprovedProperties'
import TreatmentConjuredImage from '@components/app/TreatmentPage/TreatmentConjuredImage'
import TreatmentContraindicationsProperties from '@components/app/TreatmentPage/TreatmentContraindicationsProperties'
import TreatmentDosageProperties from '@components/app/TreatmentPage/TreatmentDosageProperties'
import { TreatmentDrugInteractions } from '@components/app/TreatmentPage/TreatmentDrugInteractions'
import TreatmentNovelUses from '@components/app/TreatmentPage/TreatmentNovelUses'
import { TreatmentOverview } from '@components/app/TreatmentPage/TreatmentOverview'
import { TreatmentPageHero } from '@components/app/TreatmentPage/TreatmentPageHero'
import TreatmentPharmacodynamicsProperties from '@components/app/TreatmentPage/TreatmentPharmacodynamicsProperties'
import { TreatmentProperties } from '@components/app/TreatmentPage/TreatmentProperties'
import { TreatmentReviews } from '@components/app/TreatmentPage/TreatmentReviews'
import TreatmentToxicityProperties from '@components/app/TreatmentPage/TreatmentToxicityProperties'
import { getParamsProvidedLayoutWithLayoutProps } from '@components/primitives/Layout/getPatientLayouts'
import { PageHeader } from '@components/primitives/Layout/HeaderComponents/PageHeader'
import { logObjectSize } from '@lib/database/cacheClient/sizeLogger'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import ExternalRoutes from '@lib/routes/ExternalRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import getCanonicalUrl from '@lib/routes/helpers/getCanonicalUrl'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import isProductionEnv from '@lib/utilities/isProductionEnv'
import { ONE_DAY_IN_SECONDS } from '@lib/utilities/time'
import getCachedTreatmentPageData from '@modules/drugs/helpers/cache/getCachedTreatmentPageData'
import type { TreatmentPageObject } from '@modules/drugs/helpers/getTreatmentPageData'
import type { GetStaticPropsContext, GetStaticPropsResult } from 'next'
import { useInView } from 'react-intersection-observer'

export async function getStaticPaths() {
  return { fallback: 'blocking', paths: [] }
}

/**
 * `getStaticProps`: given a slug, fetch the static props to render this treatment page.
 */
export async function getStaticProps(
  context: GetStaticPropsContext,
): Promise<GetStaticPropsResult<TreatmentPageObject>> {
  const slug = context.params!.slug as string
  const force = !isProductionEnv() // Always get fresh data in dev + preview

  const treatmentPageData = await getCachedTreatmentPageData(slug, { force })
  if (!treatmentPageData) {
    return {
      notFound: true,
    }
  }
  logObjectSize({ obj: treatmentPageData, route: `/t/${slug}` })

  return {
    props: treatmentPageData,
    revalidate: ONE_DAY_IN_SECONDS,
  }
}

function TreatmentPage(props: NonNullable<TreatmentPageObject>) {
  const {
    drugApprovedUses,
    drugContraindications,
    drugContraindicationsSummary,
    drugDescription,
    drugDosages,
    drugDosagesSummary,
    // drugCosts,
    // drugDemographicConsiderations,
    drugInteractions,
    drugLabelData,
    // drugEfficacyData,
    drugMechanismOfActionSummary,
    drugName,
    drugNovelUses,
    drugNovelUsesSummary,
    drugOverviewProperties,
    drugPharmacodynamicsSummary,
    drugToxicitySummary,
    // drugSideEffectsData,
    faqs,
    // pastClinicalTrialResults,
    images,
    metaDescription,
    metaTitle,
    relatedArticles,
    relatedDirectories,
    relatedListings,
    relatedTreatmentTopics,
    searchName,
    treatmentReviews,
    trials,
    // references,
  } = props

  const sectionRefs = {
    about: useInView({ threshold: 0.3 }),
    approvedUses: useInView({ threshold: 0.3 }),
    contraindications: useInView({ threshold: 0.1 }),
    cost: useInView({ threshold: 0.1 }),
    demographics: useInView({ threshold: 0.1 }),
    dosages: useInView({ threshold: 0.1 }),
    efficacy: useInView({ threshold: 0.5 }),
    novelUses: useInView({ threshold: 0.1 }),
    overview: useInView({ threshold: 0.1 }),
    relatedTrials: useInView({ threshold: 0.1 }),
    reviews: useInView({ threshold: 0.1 }),
    sideEffects: useInView({ threshold: 0.5 }),
    warnings: useInView({ threshold: 0.1 }),
  }

  return (
    <div className='mb-8'>
      <PageHeader
        canonicalUrl={getCanonicalUrl(
          absoluteUrl(DynamicRoutes.treatmentTopic(props.slug)),
        )}
        metaDescription={metaDescription}
        openGraphTitle={metaTitle}
        openGraphUrl={ExternalRoutes.merianOgImage({
          text: `Clinical Trials featuring: ${searchName}`,
        })}
        pageTitle={`${searchName}: Everything you need to know`}
      />
      <NavBar />
      <div className='grid grid-cols-1 gap-12'>
        <TreatmentPageHero {...props} />
        <div className='mx-auto flex'>
          <div className='hidden w-1/4 md:block'>
            <TreatmentPageTableOfContents sectionRefs={sectionRefs} />
          </div>
          <div className='ml-auto grid w-full grid-cols-1 gap-12 lg:w-3/4'>
            <ListingSection
              id='about'
              innerRef={sectionRefs.about.ref}
              title={`What is ${searchName}`}
            >
              <TreatmentProperties
                drugDescription={drugDescription} // This is the "background" section in drugbank
                drugLabelData={drugLabelData}
                drugName={drugName}
                drugOverviewProperties={drugOverviewProperties}
              />
              <TreatmentConjuredImage images={images} placement='whatIs' />
              {drugLabelData && (
                <TreatmentOverview
                  drugLabelData={drugLabelData}
                  drugName={searchName}
                />
              )}
            </ListingSection>
            <ListingSection
              hide={drugApprovedUses.length === 0}
              id='approvedUses'
              innerRef={sectionRefs.approvedUses.ref}
              title={`Approved as Treatment by the FDA`}
            >
              <FDAApprovedTemplateString
                brandName={drugLabelData?.brandName}
                drugApprovedUses={drugApprovedUses}
                drugName={drugName}
              />
              <TreatmentApprovedProperties
                drugApprovedUses={drugApprovedUses}
              />
            </ListingSection>

            <ListingSection
              hide={
                !drugPharmacodynamicsSummary && !drugMechanismOfActionSummary
              }
              id='efficacy'
              innerRef={sectionRefs.efficacy.ref}
              title={`Effectiveness`}
            >
              <TreatmentPharmacodynamicsProperties
                drugName={searchName}
                drugPharmacodynamicsSummary={drugPharmacodynamicsSummary}
              />
              <TreatmentActionProperties
                drugMechanismOfActionSummary={drugMechanismOfActionSummary}
                drugName={searchName}
              />
            </ListingSection>
            <ListingSection
              hide={true}
              id='sideEffects'
              innerRef={sectionRefs.sideEffects.ref}
              title={`Side Effects`}
            />
            <ListingSection
              hide={drugDosages.length === 0}
              id='dosages'
              innerRef={sectionRefs.dosages.ref}
              title={`When to interrupt dosage`}
            >
              <DoseModificationSummary
                drugDosagesSummary={drugDosagesSummary}
              />
              <TreatmentDosageProperties drugDosages={drugDosages} />
            </ListingSection>
            <ListingSection
              hide={true}
              id='cost'
              innerRef={sectionRefs.cost.ref}
              title={`${searchName} Cost`}
            />

            <ListingSection
              hide={true}
              id='demographics'
              innerRef={sectionRefs.demographics.ref}
              title={`${searchName} Demographic Considerations`}
            />

            <ListingSection
              hide={
                drugContraindications.length === 0 &&
                drugInteractions.length === 0 &&
                !drugToxicitySummary
              }
              id='warnings'
              innerRef={sectionRefs.warnings.ref}
              title={`Warnings`}
            >
              <ContraIndicationsSummary
                drugContraindicationsSummary={drugContraindicationsSummary}
              />
              <TreatmentContraindicationsProperties
                drugContraindications={drugContraindications}
                drugName={searchName}
              />
              <DrugInteractionTemplateString
                drugInteractions={drugInteractions}
                drugName={searchName}
              />
              <TreatmentDrugInteractions
                drugInteractions={drugInteractions}
                drugName={searchName}
              />
              <TreatmentToxicityProperties
                drugName={searchName}
                drugToxicitySummary={drugToxicitySummary}
              />
              <TreatmentConjuredImage
                images={images}
                placement='effectiveness'
              />
            </ListingSection>

            <ListingSection
              hide={drugNovelUses.length === 0}
              id='novelUses'
              innerRef={sectionRefs.novelUses.ref}
              title={`${searchName} Novel Uses: Which Conditions Have a Clinical Trial Featuring ${searchName}?`}
            >
              <NovelUsesSummary drugNovelUsesSummary={drugNovelUsesSummary} />
              <TreatmentNovelUses drugNovelUses={drugNovelUses} />
            </ListingSection>
            <ListingSection
              hide={treatmentReviews.length === 0}
              id='reviews'
              innerRef={sectionRefs.reviews.ref}
              title={`${searchName} Reviews: What are patients saying about ${searchName}?`}
            >
              <TreatmentReviews
                searchName={searchName}
                treatmentReviews={treatmentReviews}
              />
              <TreatmentConjuredImage images={images} placement='dosage' />
            </ListingSection>
          </div>
        </div>

        <ListingSection
          id='faq'
          title={`Patient Q&A Section about ${props.searchIntentKeyword}`}
        >
          <Faqs
            faqName={`FAQs About ${props.searchIntentKeyword}`}
            faqs={faqs}
          />
        </ListingSection>
        <ListingSection
          hide={trials.length === 0}
          id='relatedTrials'
          innerRef={sectionRefs.relatedTrials.ref}
          title={`Clinical Trials for ${searchName}`}
        >
          <TrialsForTreatment drugName={searchName} trials={trials} />
        </ListingSection>

        <RelatedListingLinks
          relatedListings={relatedListings ?? []}
          trackingSource={TrackingSources.TREATMENT_PAGE}
        />
        <RelatedConditionCarousel
          relatedDirectories={relatedDirectories ?? []}
          trackingSource={TrackingSources.TREATMENT_PAGE}
        />
        <RelatedArticleLinks
          relatedArticles={relatedArticles ?? []}
          trackingSource={TrackingSources.TREATMENT_PAGE}
        />
        <RelatedTreatmentTopicLinks
          relatedTreatmentTopics={relatedTreatmentTopics ?? []}
          trackingSource={TrackingSources.TREATMENT_PAGE}
        />
      </div>
    </div>
  )
}

TreatmentPage.getLayout = getParamsProvidedLayoutWithLayoutProps({})

export default TreatmentPage
